// Ensure React is properly imported before using Zustand
import * as React from 'react';
import { create } from 'zustand';
import { UserProfile, UserPreferences } from '../types/UserProfile';
import { userProfileService } from '../services/UserProfileService';
import { PROFILE_CONSTANTS } from '../constants/profileConstants';

interface ProfileState {
  // Profiles
  profiles: UserProfile[];
  currentProfile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  initialize: (signal?: AbortSignal) => Promise<void>;
  fetchProfiles: () => Promise<void>;
  createProfile: (profile: Partial<UserProfile>) => Promise<UserProfile>;
  updateProfile: (id: string, profile: Partial<UserProfile>) => Promise<UserProfile | null>;
  deleteProfile: (id: string) => Promise<boolean>;
  setCurrentProfile: (id: string) => Promise<void>;
  updatePreferences: (id: string, preferences: Partial<UserPreferences>) => Promise<void>;
}

/**
 * Store for managing user profiles
 * Using factory function pattern to prevent React initialization issues
 */
const createProfileStore = () => {
  return create<ProfileState>((set, get) => ({
    // Initial state
    profiles: [],
    currentProfile: null,
    isLoading: false,
    error: null,
    
    // Initialize the store with optional abort signal for cancellation
    initialize: async (signal?: AbortSignal) => {
      // Check if profiles are already initialized to avoid redundant calls
      const { profiles, isLoading } = get();
      if (profiles.length > 0 || isLoading) {
        return; // Already initialized or in progress
      }
      
      set({ isLoading: true, error: null });
      
      try {
        // First check if the operation is already aborted before starting
        if (signal?.aborted) {
          
          // Return early without throwing to allow UI to render with defaults
          set({ isLoading: false });
          return;
        }

        // Wrap API calls in try/catch to prevent unhandled promise rejections
        let profiles = [];
        try {
          // Fetch profiles from the database
          profiles = await userProfileService.getAllProfiles();
        } catch (apiError) {
          
          // Fall back to mock data to prevent UI errors - ensure all users start with Free tier by default
          profiles = [
            { id: 'u1', name: 'Primary User', plusTier: false, preferences: {} },
            { id: 'u2', name: 'Secondary User', plusTier: false, preferences: {} }
          ];
        }
        
        // Check ALL storage mechanisms for profile IDs
        
        
        // Get all possible stored profile IDs
        const storedProfileId = localStorage.getItem('currentProfileId');
        const alternateStoredId = localStorage.getItem('meduler_profile_id');
        
        // Function to get a specific cookie value
        const getCookieValue = (name: string): string | null => {
          const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
          return match ? match[2] : null;
        };
        
        // Get cookie values
        const cookieProfileId = getCookieValue('meduler_profile_id');
        const legacyCookieId = getCookieValue('currentProfileId');
        
        // Log all storage states
        // Removed complex console statement for security;
        
        // CRITICAL: Always default to 'u1' as the primary user profile
        // This is the first user created and should be the default experience
        const profileIdToUse = 'u1';
        
        // Always ensure localStorage is set to U1 on initialization
        localStorage.setItem(PROFILE_CONSTANTS.STORAGE_KEY, profileIdToUse);
        
        // Also update cookie for consistency across storage mechanisms
        const date = new Date();
        date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000));
        document.cookie = `${PROFILE_CONSTANTS.COOKIE_KEY}=${profileIdToUse}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
        
        // Set the current profile
        let currentProfile = null;
        
        // Always use U1 as the primary profile
        currentProfile = profiles.find(p => p.id === profileIdToUse);
        
        // Fallback in case U1 is somehow not available (should never happen)
        if (!currentProfile && profiles.length > 0) {
          currentProfile = profiles[0];
          localStorage.setItem(PROFILE_CONSTANTS.STORAGE_KEY, currentProfile.id);
        }
        
        // Update the store
        set({
          profiles,
          currentProfile,
          isLoading: false
        });
      } catch (error) {
        // Only log a simplified error to console to reduce noise
        
        set({ 
          error: error instanceof Error ? error.message : 'Failed to initialize profiles',
          isLoading: false 
        });
      }
    },
    
    // Fetch all profiles
    fetchProfiles: async () => {
      set({ isLoading: true, error: null });
      
      try {
        const profiles = await userProfileService.getAllProfiles();
        
        set({
          profiles,
          isLoading: false
        });
      } catch (error) {
        
        set({ 
          error: error instanceof Error ? error.message : 'Failed to fetch profiles',
          isLoading: false 
        });
      }
    },
    
    // Create a new profile
    createProfile: async (profileData) => {
      set({ isLoading: true, error: null });
      
      try {
        const newProfile = await userProfileService.createProfile(profileData);
        
        set(state => ({
          profiles: [...state.profiles, newProfile],
          isLoading: false 
        }));
        return newProfile;
      } catch (error) {
        
        set({ 
          error: error instanceof Error ? error.message : 'Failed to create profile',
          isLoading: false 
        });
        throw error;
      }
    },
    
    // Update an existing profile
    updateProfile: async (id, profileData) => {
      set({ isLoading: true, error: null });
      
      try {
        const updatedProfile = await userProfileService.updateProfile(id, profileData);
        
        if (updatedProfile) {
          set(state => ({
            profiles: state.profiles.map(p => p.id === id ? updatedProfile : p),
            currentProfile: state.currentProfile?.id === id ? updatedProfile : state.currentProfile,
            isLoading: false
          }));
        } else {
          set({ isLoading: false });
        }
        
        return updatedProfile;
      } catch (error) {
        
        set({ 
          error: error instanceof Error ? error.message : 'Failed to update profile',
          isLoading: false 
        });
        return null;
      }
    },
    
    // Delete a profile
    deleteProfile: async (id) => {
      set({ isLoading: true, error: null });
      
      try {
        const success = await userProfileService.deleteProfile(id);
        
        if (success) {
          // Get updated profiles and determine new current profile
          const { profiles, currentProfile } = get();
          const updatedProfiles = profiles.filter(p => p.id !== id);
          
          // If we deleted the current profile, select a new one
          let newCurrentProfile = currentProfile;
          if (currentProfile?.id === id && updatedProfiles.length > 0) {
            newCurrentProfile = updatedProfiles[0];
            localStorage.setItem('currentProfileId', newCurrentProfile.id);
          } else if (updatedProfiles.length === 0) {
            newCurrentProfile = null;
            localStorage.removeItem('currentProfileId');
          }
          
          set({
            profiles: updatedProfiles,
            currentProfile: newCurrentProfile,
            isLoading: false
          });
        } else {
          set({ isLoading: false });
        }
        
        return success;
      } catch (error) {
        
        set({ 
          error: error instanceof Error ? error.message : 'Failed to delete profile',
          isLoading: false 
        });
        return false;
      }
    },
    
    // Set current active profile
    setCurrentProfile: async (id) => {
      set({ isLoading: true, error: null });
      
      try {
        
        
        // Validate the ID - only allow 'u1' or 'u2'
        const validatedId = id === 'u1' || id === 'u2' ? id : 'u1';
        
        if (validatedId !== id) {
          
        }
        
        const { profiles } = get();
        const profileToSet = profiles.find(p => p.id === validatedId);
        
        if (profileToSet) {
          // Log before state
          // Removed complex console statement for security;
          
          // Store the profile ID in all storage mechanisms for consistency
          localStorage.setItem('currentProfileId', validatedId);
          localStorage.setItem(PROFILE_CONSTANTS.STORAGE_KEY, validatedId);
          localStorage.setItem('meduler_profile_id', validatedId);
          
          // Also set cookie for cross-storage consistency
          const date = new Date();
          date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000));
          document.cookie = `meduler_profile_id=${validatedId}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
          document.cookie = `${PROFILE_CONSTANTS.COOKIE_KEY}=${validatedId}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
          document.cookie = `currentProfileId=${validatedId}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
          
          // Force a UI update by creating a new profile object reference
          const updatedProfile = { ...profileToSet };
          
          set({
            currentProfile: updatedProfile,
            isLoading: false
          });
          
          // Force the user interface to refresh by triggering state change in related components
          // This helps ensure the UI reflects the change immediately
          setTimeout(() => {
            
            // Slightly modify and update the profile again to force store subscribers to react
            set(state => ({
              currentProfile: { ...updatedProfile, lastRefresh: Date.now() }
            }));
          }, 50);
          
          // Log after state
          // Removed complex console statement for security;
        } else {
          
          set({ isLoading: false });
        }
      } catch (error) {
        
        set({ 
          error: error instanceof Error ? error.message : 'Failed to set current profile',
          isLoading: false 
        });
      }
    },
    
    // Update user preferences
    updatePreferences: async (id, preferences) => {
      set({ isLoading: true, error: null });
      
      try {
        // First check if the profile exists
        const { profiles } = get();
        const profile = profiles.find(p => p.id === id);
        
        if (profile) {
          // Type assertion for the missing method
          const updatedPrefs = await (userProfileService as any).updatePreferences(id, preferences);
          
          // Update the profile with new preferences
          const updatedProfile = {
            ...profile,
            preferences: {
              ...profile.preferences,
              ...preferences
            }
          };
          
          set(state => ({
            profiles: state.profiles.map(p => p.id === id ? updatedProfile : p),
            currentProfile: state.currentProfile?.id === id ? updatedProfile : state.currentProfile,
            isLoading: false
          }));
        } else {
          set({ isLoading: false });
        }
      } catch (error) {
        
        set({ 
          error: error instanceof Error ? error.message : 'Failed to update preferences',
          isLoading: false 
        });
      }
    },
  }));
};

// Export the store with the factory function pattern
// This ensures React is properly initialized before any hooks are used
export const useProfileStore = createProfileStore();
